/**
 * Material-UI theme configuration
 * Defines custom theme with typography, colors, and component overrides
 */
import { createTheme, Theme, ThemeOptions, PaletteMode } from '@mui/material/styles';

/**
 * Creates theme based on provided mode
 * @param mode - Theme mode (light or dark)
 * @returns Configured theme
 */
export const createAppTheme = (mode: PaletteMode): Theme => {
    const themeOptions: ThemeOptions = {
        palette: {
            mode,
            primary: {
                main: mode === 'light' ? '#1976d2' : '#90caf9',
                light: mode === 'light' ? '#42a5f5' : '#a6d4fa',
                dark: mode === 'light' ? '#1565c0' : '#648dae',
                contrastText: '#ffffff',
            },
            secondary: {
                main: mode === 'light' ? '#dc004e' : '#f48fb1',
                light: mode === 'light' ? '#e33371' : '#f6a5c0',
                dark: mode === 'light' ? '#9a0036' : '#aa647b',
                contrastText: '#ffffff',
            },
            error: {
                main: '#f44336',
            },
            warning: {
                main: '#ff9800',
            },
            info: {
                main: '#2196f3',
            },
            success: {
                main: '#4caf50',
            },
            background: {
                default: 'transparent',
                paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
            },
            text: {
                primary: '#001C53',
                secondary: mode === 'light' ? 'rgba(0, 28, 83, 0.6)' : 'rgba(255, 255, 255, 0.6)',
            },
        },
        typography: {
            fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
            allVariants: {
                color: '#001C53',
                letterSpacing: '0.01em',
            },
            // Headings — Noto Sans
            // Note: Responsive scaling is handled via sx prop in components
            h1: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '42px',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
                verticalAlign: 'middle',
                color: '#001C53',
            },
            h2: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '36px',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
                verticalAlign: 'middle',
                color: '#001C53',
            },
            h3: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '30px',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
                verticalAlign: 'middle',
                color: '#001C53',
            },
            h4: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '24px',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
                verticalAlign: 'middle',
                color: '#001C53',
            },
            h5: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
                verticalAlign: 'middle',
                color: '#001C53',
            },
            h6: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: '18px',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
                verticalAlign: 'middle',
                color: '#001C53',
            },
            // Body — Noto Sans
            body1: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontSize: '15px',
                lineHeight: '24px',
                fontWeight: 400,
                letterSpacing: '0.01em',
                color: '#001C53',
            },
            body2: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontSize: '13px',
                lineHeight: '20px',
                fontWeight: 400,
                letterSpacing: '0.01em',
                color: '#001C53',
            },
            caption: {
                fontFamily: '"Noto Sans", Arial, Helvetica, sans-serif',
                fontSize: '11px',
                lineHeight: '16px',
                fontWeight: 400,
                letterSpacing: '0.01em',
                color: '#001C53',
            },
        },
        spacing: 8,
        shape: {
            borderRadius: 8,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'medium',
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: mode === 'light'
                            ? '0 2px 8px rgba(0,0,0,0.1)'
                            : '0 2px 8px rgba(0,0,0,0.3)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        background: 'transparent',
                        backgroundImage: 'none',
                        boxShadow: 'none',
                    },
                    colorPrimary: {
                        backgroundColor: 'transparent',
                        background: 'transparent',
                    },
                    colorDefault: {
                        backgroundColor: 'transparent',
                        background: 'transparent',
                    },
                    colorInherit: {
                        backgroundColor: 'transparent',
                        background: 'transparent',
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        background: 'transparent',
                        backgroundImage: 'none',
                    },
                },
            },
            MuiTypography: {
                defaultProps: {
                    // Ensure default props don't override our styles
                },
                styleOverrides: {
                    root: ({ ownerState }) => {
                        // Base styles that apply to all Typography variants
                        const baseStyles: any = {
                            letterSpacing: '0.25px',
                            color: '#001C53',
                        };

                        // Apply variant-specific font families and weights
                        if (ownerState.variant?.startsWith('h')) {
                            // All headings use Noto Sans
                            baseStyles.fontFamily = '"Noto Sans", Arial, Helvetica, sans-serif';
                            baseStyles.fontWeight = 600;
                            baseStyles.lineHeight = '110%';
                            baseStyles.letterSpacing = '-0.02em';
                            baseStyles.verticalAlign = 'middle';
                        } else {
                            // All body text uses Noto Sans
                            baseStyles.fontFamily = '"Noto Sans", Arial, Helvetica, sans-serif';
                            baseStyles.fontWeight = 400;
                            baseStyles.letterSpacing = '0.01em';
                        }

                        return baseStyles;
                    },
                },
            },
        },
    };

    return createTheme(themeOptions);
};

// Default theme export (light mode)
export const theme = createAppTheme('light');
